<template>
  <div class="content cont">
    <div class="document-nav">
      <h2>Wallet</h2>
    </div>

    <div>
      <div v-if="showSuccessMessage" class="success-message">
        Virtual account generated successfully!
      </div>
    </div>

    <div class="page-loader" v-show="pageLoading">
      <div>
        <PageLoader />
      </div>
    </div>

    <div class="card-group text-center" v-show="!pageLoading">
      <div class="card">
        <div class="card-body">
          <p class="card-text text-center">Available Wallet Balance</p>
          <h4 class="card-title">
            ₦ {{ formatNumber($store.state.walletBalance) }}
          </h4>
        </div>
      </div>

      <div class="card">
        <div class="card-body">
          <p class="card-text text-center">Next Payroll Run Amount</p>
          <h4 class="card-title">
            ₦{{
              overViewData.total_payout ? overViewData.total_payout : "0.00"
            }}
          </h4>
        </div>
      </div>

      <div class="card">
        <div class="card-body">
          <p class="card-text text-center">Locked Balance</p>
          <h4 class="card-title">
            ₦{{ formatNumber(walletBalance.locked_balance) }}
          </h4>
        </div>
      </div>
    </div>

    <div class="center-content">
      <div class="error">
        <p v-if="dataFetchError">
          {{ dataFetchErrorMessage }}
        </p>
        <p v-if="dataSaveError">{{ dataSaveError }}</p>
        <div v-if="serverErrors">
          <p v-for="(value, name, index) in serverErrors" :key="index">
            {{ value[0] }}
          </p>
        </div>
      </div>
    </div>

    <p class="notificationText">
      <strong>Note:</strong> A charge of 1.2% (capped at #200) will apply to this top-up transaction.
    </p>

    <div class="virtual-card" v-show="!pageLoading">
      <div class="virtual-card-message">
        <p v-show="virtualAccountData">
          You can fund your wallet by transferring money from your bank account
          to the account number displayed below
        </p>

        <p v-show="!virtualAccountData">
          Fund your wallet by generating a virtual account below
        </p>
      </div>

      <div class="center-content" v-if="!virtualAccountData">
        <div class="btn-container">
          <div class="button-wrapper">
            <button
              class="virtual-btn"
              role="button"
              @click="createVirtualAccount"
              v-show="!loading"
            >
              GENERATE VIRTUAL ACCOUNT
            </button>
            <div v-show="loading">
              <BlueLoader />
            </div>
          </div>
        </div>
      </div>

      <div v-if="virtualAccountData">
        <div class="virtual-card-bottom">
          <div class="virtual-text-block">
            <p class="virtual-card-heading">BANK NAME</p>
            <p class="virtual-card-text" v-if="virtualAccountData">
              {{ virtualAccountData.bank_name }}
            </p>
          </div>
          <div class="virtual-text-block">
            <p class="virtual-card-heading">ACCOUNT NUMBER</p>
            <p class="virtual-card-text" v-if="virtualAccountData">
              {{ virtualAccountData.account_number }}
            </p>
          </div>
          <div class="virtual-text-block">
            <p class="virtual-card-heading">ACCOUNT NAME</p>
            <p class="virtual-card-text" v-if="virtualAccountData">
              {{ virtualAccountData.account_name }}
            </p>
          </div>
        </div>
        <div class="virtual-card-top">
          <div>
            <p class="virtual-light-text">Bank Account Details</p>
            <p class="virtual-card-info">
              Use this account to fund your wallet only
            </p>
          </div>

          <div class="btn-cover">
            <button
              class="virtual-btn"
              role="button"
              @click="copyAccountDetails"
            >
              Copy Account
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="filter-container" v-show="!pageLoading">
      <p class="text">Wallet Transaction History:</p>
    </div>

    <div class="table-form" v-show="!pageLoading">
      <table class="table" v-if="paginatedData.length > 0">
        <thead>
          <tr>
            <th>S/N</th>
            <th>Description</th>
            <th>Amount</th>
            <th>Fee</th>
            <th>New&nbsp;Balance</th>
            <th>Prev&nbsp;Balance</th>
            <th>Reference&nbsp;No</th>
            <th>Date</th>
          </tr>
        </thead>
        <tbody>
          <tr
            class="batch"
            v-for="(walletHistory, index) in paginatedData"
            :key="index"
          >
            <td>{{ index + 1 }}</td>
            <td>{{ walletHistory.description }}</td>
            <td>
              <div
                :class="{
                  credit: walletHistory.type === 'credit',
                  debit: walletHistory.type === 'debit',
                }"
              >
                {{ walletHistory.type == "credit" ? "+" : "-" }}&nbsp;&#8358;{{
                  walletHistory.amount
                    .toString()
                    .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,")
                }}
              </div>
            </td>
            <td>
              &#8358;{{
                formatWalletAmount(walletHistory.fee) | addKoboToValue
              }}
            </td>
            <td>
              &#8358;{{
                formatWalletAmount(walletHistory.new_balance) | addKoboToValue
              }}
            </td>

            <td>
              &#8358;{{
                formatWalletAmount(walletHistory.previous_balance)
                  | addKoboToValue
              }}
            </td>
            <td>{{ walletHistory.reference }}</td>
            <td>{{ createdDate(walletHistory.created_at) }}</td>
          </tr>
        </tbody>
        <div class="paginationContainer">
          <div class="pagination-controls">
                <button
                  class="paginationBtn"
                  @click="prevPage"
                  :disabled="!pagination.prev_page_url"
                >
                  Previous
                </button>
                <span
                  >Page {{ pagination.current_page }} of
                  {{ pagination.last_page }}</span
                >

                <button
                  class="paginationBtn"
                  @click="nextPage"
                  :disabled="!pagination.next_page_url"
                >
                  Next
                </button>
              </div>
        </div>
      </table>
      <div v-else>
        <EmptyContent title="No transaction history available" />
      </div>
    </div>
  </div>
</template>
<script>
import * as types from "../../../store/constant";
import BlueLoader from "../../partials/blue-loader";
import EmptyContent from "../../partials/emptyContent";
import PageLoader from "../../partials/page-loader";
import { addKoboToValue } from "../../../util/filters";

export default {
  name: "WalletPage",

  components: {
    BlueLoader,
    PageLoader,
    EmptyContent,
  },

  data() {
    return {
      formData: {
        reference: "",
        mode: "paystack",
        amount: "",
        provider_reference: "",
      },

      paginatedData: [],
      pagination: {
        current_page: null,
        last_page: null,
        next_page_url: null,
        prev_page_url: null,
        itemsPerPage: null,
        totalRecords: null,
        total_page: null,
      },

      virtualAccountData: null,
      showVirtualAccount: true,
      showWalletHistory: false,

      pageLoading: true,
      showSuccessMessage: false,

      dataSaveError: "",
      dataFetchError: false,
      dataFetchErrorMessage: "",
      serverErrors: null,

      loading: false,

      withdrawalFormData: {
        amount: "",
      },

      walletBalance: {},
      walletHistories: [],
      withdrawAbleBalance: {},

      companyDetails: null,
      overViewData: {},
    };
  },
  metaInfo: {
    title: "HR Desk Wallet - Simplified Employee Compensation Management",
    meta: [
      {
        name: "description",
        content:
          "Manage employee compensation effortlessly with HR Desk Wallet. Track payments, handle reimbursements, track wallet balance and view accurate next payroll run amount.",
      },
      { property: "og:title", content: "HR Desk Wallet - Simplified Employee Compensation Management" },
      {
        property: "og:description",
        content:
          "Manage employee compensation effortlessly with HR Desk Wallet. Track payments, handle reimbursements, track wallet balance and view accurate next payroll run amount.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://app.hrdesk.africa/wallet" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "HR Desk Wallet - Simplified Employee Compensation ManagementWallet - hrdesk" },
      {
        name: "twitter:description",
        content:
          "Manage employee compensation effortlessly with HR Desk Wallet. Track payments, handle reimbursements, track wallet balance and view accurate next payroll run amount.",
      },
      { name: "twitter:image:alt", content: "Wallet at hrdesk" },
      { name: 'keywords', content: 'HR wallet, employee compensation, balance tracking, wallet funding tracking, reimbursement management, HR payment solutions' },
    ],
  },
  created() {
    this.getCompanyData();
    this.getWalletHistory();
    this.getOverviewData();
  },

  mounted() {
    this.fetchVirtualAccount();
  },

  filters: {
    addKoboToValue,
  },
  computed: {
    totalPages() {
      return Math.ceil(
        this.pagination.totalRecords / this.pagination.itemsPerPage
      );
    },
  },
  methods: {
    fetchData(page = 1) {
      const url = `${types.COMPANY_WALLET}?page=${page}`;
      this.$http
        .get(url)
        .then((resp) => {
          this.history = resp.data.data.data;
          let data = resp.data.data;
          this.paginatedData = data.data;

          this.pagination = {
            current_page: data.current_page,
            last_page: data.last_page,
            next_page:
              data.current_page < data.last_page ? data.current_page + 1 : null,
            prev_page: data.current_page > 1 ? data.current_page - 1 : null,
            items_per_page: data.per_page,
            total_records: data.total,
          };

          this.loader = false;
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
        });
    },
    nextPage() {
      if (this.pagination.next_page_url) {
        this.fetchData(this.pagination.next_page_url);
      }
    },
    prevPage() {
      if (this.pagination.prev_page_url) {
        this.fetchData(this.pagination.prev_page_url);
      }
    },
    formatWalletAmount(amount) {
      return parseFloat(amount);
    },

    createVirtualAccount() {
      this.loading = true;
      this.$http
        .post(types.CREATE_VIRTUAL_ACCOUNT_API)
        .then((resp) => {
          this.virtualAccountData = resp.data.data;
          this.showVirtualAccount = true;
          this.loading = false;
        })
        .catch((err) => {
          this.loading = false;
          if (err.response) {
            this.dataFetchError = true;
            this.dataFetchErrorMessage = err.response.data.message;
          }
        });
    },

    copyAccountDetails() {
      const accountDetailsText = `${this.virtualAccountData.account_number}`;

      navigator.clipboard
        .writeText(accountDetailsText)
        .then(() => {
          alert("Account details copied to clipboard!");
        })
        .catch((error) => {
          console.error("Failed to copy account details: ", error);
        });
    },

    fetchVirtualAccount() {
      this.loading = true;
      this.$http
        .get(types.FETCH_VIRTUAL_ACCOUNT_API)
        .then((resp) => {
          let { account_name, account_number } = resp.data.data;

          if (account_name == "" || account_number == "") {
            this.virtualAccountData = null;
            this.showVirtualAccount = false;
          } else {
            this.virtualAccountData = resp.data.data;
            this.showVirtualAccount = true;
          }

          this.loading = false;
        })
        .catch((err) => {
          this.loading = false;
          if (err.response) {
            this.dataFetchError = true;
          }
        });
    },

    getOverviewData() {
      this.pageLoading = true;

      this.$http
        .get(types.OVERVIEW_API)
        .then((resp) => {
          this.overViewData = resp.data.data;
          this.pageLoading = false;
        })
        .catch((err) => {
          this.pageLoading = false;
          if (err.response) {
            this.dataFetchError = true;
          }
        });
    },

    getWalletHistory() {
      this.fetchData()
    },

    formatNumber(amount) {
      const formatter = new Intl.NumberFormat("en-NG", {
        style: "currency",
        currency: "NGR",
        minimumFractionDigits: 2,
      });

      const amountCheck = amount || 0;

      const v = formatter.format(amountCheck);
      return String(v).substring(4);
    },

    formatAmount() {
      if (+this.formData.amount.replace(/,/g, "")) {
        this.formData.amount = parseInt(
          this.formData.amount.replace(/,/g, ""),
          10
        )
          .toString()
          .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
      } else {
        this.formData.amount = this.formData.amount.substring(
          0,
          this.formData.amount.length - 1
        );
      }
    },

    getWalletBalance() {
      this.$http
        .get(`${types.COMPANY_WALLET}/${this.companyDetails.wallet_number}`)
        .then((resp) => {
          if (resp.data.data) {
            this.walletBalance = resp.data.data;
            this.withdrawAbleBalance = resp.data.data;
          }
        })
        .catch((err) => {
          this.loading = false;
          if (err.response) {
            this.dataFetchError = true;
          }
        });
    },

    getCompanyData() {
      let user = this.$store.getters.currentAuthUser;

      this.$http
        .get(`${types.COMPANY_GET_API}/${user.employees.company_id}`)
        .then((resp) => {
          this.companyDetails = resp.data.data;
          this.getWalletBalance();
        })
        .catch();
    },

    createdDate(dateString) {
      const date = new Date(dateString);
      const options = {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      };
      return date.toLocaleString("en-US", options);
    },

    generateReference() {
      this.formData.reference = "" + Math.floor(Math.random() * 1000000000 + 1);
    },
  },
};
</script>
<style lang="scss" scoped>
@import url(https://maxcdn.bootstrapcdn.com/font-awesome/4.5.0/css/font-awesome.min.css);

div.content {
  padding-bottom: 5rem;
  padding-top: 1rem;
  padding-left: 5.5rem;
  padding-right: 5.5rem;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    padding: 0.5rem;
  }

  .page-loader {
    margin-top: 50px;

    > div {
      width: 55px;
      margin: auto;
    }
  }

  input,
  input:focus {
    outline: none !important;
  }
  .paginationContainer {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  text-align: center;
  font-size: 0.75rem;
}
.pagination-controls {
  display: flex;
  justify-content: space-around;
  align-items: center;
  margin-top: 0 auto;
  background: rgb(217, 216, 216);
  margin: 0 auto;
  text-align: center;
}
.paginationBtn {
  padding: 5px 10px;
  background: #fff;
  border-radius: 4px;
}

  .notificationText {
    margin-bottom: 0;
    text-align: center;
    font-size: 0.75rem;
    color: #e57414;
  }

  .paginationContainer {
    margin-top: 20px;
    display: flex;
    justify-content: center;
    text-align: center;
    font-size: 0.75rem;
  }
  .pagination-controls {
    display: flex;
    justify-content: space-around;
    align-items: center;
    margin-top: 0 auto;
    background: rgb(217, 216, 216);
    margin: 0 auto;
    text-align: center;
  }
  .paginationBtn {
    padding: 5px 10px;
    background: #fff;
    border-radius: 4px;
  }

  .form-control {
    box-shadow: none !important;
  }

  .error {
    margin-top: 15px;
    width: 980px;
    text-align: center;
    color: red;
    font-size: 0.938vw;
    font-style: italic;

    p {
      margin: 0;
      text-align: center;
    }

    p::first-letter {
      text-transform: capitalize;
    }

    > div {
      padding-bottom: 10px;
    }
  }

  .success {
    margin-top: 10px;
    text-align: center;
    color: green;
    font-size: 12px;
    font-style: italic;

    p {
      margin: 0;
    }

    p::first-child {
      text-transform: capitalize;
    }
  }

  .wallet-fund-container {
    .wallet-fund-column {
      width: 20rem;
      margin-left: auto;

      .wallet-fund-input {
        a {
          width: 7rem;
          border-radius: 2px;
          background-color: #5099de;
          color: #fff;
          font-family: Nunito;
          font-size: 0.9rem;
          line-height: none;
          padding: 5px;
          border-color: none;
          margin-left: 8px;
        }

        input {
          border-top-left-radius: 0px;
          border-bottom-left-radius: 0px;
        }

        .input-group-append {
          width: 40px;

          span {
            background-color: #fbfafa;
            border: 1px #b4bec8 solid;
            color: #000;
            border-top-right-radius: 0px;
            border-bottom-right-radius: 0px;
          }
        }
      }
    }
  }

  .document-nav {
    height: 35px;
    color: #302f2f;
    display: flex;
    border-bottom: 1px solid #ebe4e4;
    margin-top: 25px;
    margin-bottom: 20px;

    h2 {
      font-size: 2.008rem;
      font-weight: 900;
      font-style: normal;
      font-stretch: normal;
      letter-spacing: normal;
      text-align: left;
    }
  }

  .btn-cover {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  .btn-container {
    margin: 1rem 0;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 35px;
  }

  .button-wrapper {
    text-align: center;
  }

  .virtual-btn {
    background: #5099de;
    border-radius: 5px;
    box-shadow: rgba(0, 0, 0, 0.1) 0 2px 4px;
    color: #ffffff;
    cursor: pointer;
    font-family: Inter, -apple-system, system-ui, Roboto, "Helvetica Neue",
      Arial, sans-serif;
    height: 3rem;
    line-height: 3rem;
    outline: 0;
    overflow: hidden;
    padding: 0 1.5rem;
    pointer-events: auto;
    position: relative;
    text-align: center;
    touch-action: manipulation;
    user-select: none;
    -webkit-user-select: none;
    white-space: nowrap;
    z-index: 9;
    border: 0;
    flex: 0 0 auto;
    width: auto;
    font-size: 0.85rem;

    @media (max-width: 768px) {
      width: 12rem;
      height: 4rem;
      padding: 0.3rem;
      font-size: 0.75rem;
    }
  }

  .virtual-btn:hover {
    background: #004262;
  }

  .virtual-light-text {
    color: #e57414;
    font-weight: 500;
  }

  .virtual-card-info {
    font-size: 0.75rem;
  }

  .virtual-card {
    background: #d7e9f5;
    border-radius: 2px;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
    padding: 12px;
    margin-bottom: 12px;
    margin-right: 20px;

    p {
      margin-bottom: 0;
    }
  }

  .center-content {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .virtual-card-message {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: #f0f0f0;
    padding: 5px;
    border-radius: 2px;
    text-align: center;
    box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.1);
    margin-top: 13px;
  }

  .virtual-card-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 15px;
  }

  .virtual-card-bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 15px;
  }

  .virtual-card-flex {
    display: flex;
    justify-content: space-around;
  }

  .virtual-text-block {
    display: block;
  }

  .virtual-card-heading {
    color: #727272;
    font-size: 0.6rem;
  }

  .virtual-card-text {
    color: #004262;
    font-weight: 800;
    padding-bottom: 5px;
    font-size: 1rem;
  }

  .success-message {
    background-color: #d1fae5;
    /* Green 200 */
    color: #047857;
    /* Green 700 */
    padding: 0.4rem;
    margin-top: 1rem;
    border-radius: 0.5rem;
  }

  .card-group {
    max-width: 700px;
    border-radius: 2px;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
    background-color: #ffffff;
    margin: 0 auto 3em auto;

    .card {
      margin: 15px 0px;
      border-left: none;
      border-top: none;
      border-bottom: none;
      border-right: 2px dotted #f1eaea;

      .card-body {
        h4 {
          font-weight: 700;
          font-size: 1.5rem;
          line-height: 1.4rem;
        }

        p {
          font-size: 1rem;
          line-height: 2rem;
        }
      }
    }

    .card:last-of-type {
      border-right: none;
    }
  }

  .text {
    color: #5099de;
    line-height: 3;
  }

  .filter-button {
    padding: 5px;
    display: inline-block;
    width: 80px;

    img {
      width: 10px;
    }
  }

  .table-form {
    margin-top: 2rem;
    padding-bottom: 1rem;

    table {
      border-collapse: separate;
      border-spacing: 0 1em;

      thead th {
        border: none;
        line-height: 2px;
        font-size: 1rem;
      }

      tr > th:first-child {
        padding-left: 40px;
        padding-right: 0px;
      }

      tbody tr:first-child > td {
        border-top: 3px solid #5099de;
      }

      tr > td:first-child {
        padding-left: 42px;
        padding-right: 0px;
      }

      tr > td:last-of-type {
        padding-right: 60px;
      }

      tr > th:nth-of-type(2) {
        padding-right: 50px;
        padding-left: 20px;
      }

      tr > td:nth-of-type(2) {
        padding-right: 50px;
        padding-left: 10px;
      }

      tr > th:nth-of-type(4) {
        padding-right: 50px;
        padding-left: 20px;
      }

      tr > td:nth-of-type(4) {
        padding-right: 50px;
        padding-left: 20px;
      }

      tr > th:nth-of-type(5) {
        padding-left: 30px;
      }

      tr > td:nth-of-type(5) {
        padding-left: 30px;
      }

      th {
        width: 20%;
      }

      input {
        border: none;
      }

      td {
        width: 20%;
        padding-top: 17px;
      }

      tr.batch {
        height: 60px;
        border-radius: 2px;
        box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
        background-color: #ffffff;
        font-weight: 500;
        font-size: 0.85rem;
        color: #000;

        button {
          background-color: #1ec4b4;
          font-size: 0.9rem;
          padding: 3px;
          border-color: #1ec4b4;
          width: 80px;
        }

        span {
          font-weight: normal;
          font-size: 13px;
          padding: 8px;
          color: #fff !important;
        }
      }
    }

    @media (max-width: 768px) {
      overflow: scroll;
    }
  }

  .credit {
    padding: 5px 10px;
    text-align: center;
    color: #047857;
    font-weight: 800;
    display: flex;
  }

  .debit {
    color: #df1f1f;
    padding: 5px 10px;
    text-align: center;
    font-weight: 800;
    display: flex;
  }

  .footer {
    margin-top: 1em;

    .withdrawalform {
      width: 470px;
      border-radius: 2px;
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
      background-color: #ffffff;

      .withdrawAmount {
        width: 300px;
        display: inline-flex;
        padding-top: 20px;
        padding-bottom: 20px;
        padding-left: 50px;

        .input {
          width: 1380px;
          display: inline-flex;
          border: solid 1px #c7c7c7;
          background-color: #ffffff;
          border-top-left-radius: 0px;
          border-bottom-left-radius: 0px;

          ::placeholder {
            opacity: 0.4;
            font-size: 0.79;
          }
        }

        span {
          font-size: 1.2vw;
          line-height: 0px;
          background-color: #fbfafa;
          color: #000;
          border-top-right-radius: 0px;
          border-bottom-right-radius: 0px;
        }
      }

      .withdraw-button {
        width: 140px;
        height: 31px;
        border-radius: 2px;
        font-family: Nunito;
        font-size: 0.85vw;
        padding: 4px;
        color: #5099de;
        border-color: #5099de;
        margin-left: 9px;
      }
    }
  }

  @media (max-width: 768px) {
    padding: 2rem;
  }
}
</style>